import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DIALOG_PANEL_CLASS } from '@ui/constants';
import { ConfirmDialogComponent } from '@ui/dialog-v2/confirm-dialog/confirm-dialog.component';
import { DialogParams, DialogWidth } from '@ui/dialog-v2/dialog.tokens';
import { Observable } from 'rxjs';
import {
    ConfirmWithNoteDialogComponent,
} from '@ui/dialog-v2/confirm-with-note-dialog/confirm-with-note-dialog.component';
import { DialogV2 } from '@ui/dialog-v2/dialog-v2';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { BsIconTypes } from '@ui/elements/icon-bs/bs-icon-types';


/**
 * Service wrapper for working with popups
 */
@Injectable({
    providedIn: 'root',
})
export class DialogService {
    readonly #dialog: MatDialog = inject(MatDialog);

    public openConfirmPopup(
        confirmFunction: () => void,
        pending$?: Observable<boolean> | undefined,
    ): void {
        this.clearTriggerFocus();

        this.#dialog.open(DialogV2, {
            panelClass: DIALOG_PANEL_CLASS,
            width: DialogWidth.SM,
            data: {
                component: ConfirmDialogComponent,
                params: {
                    confirmFunction,
                    pending$,
                    title: marker('global.labels.attention'),
                    description: marker('modal.confirm.text.delete'),
                    icon: {
                        variant: BsIconTypes.ExclamationTriangle,
                        style: 'danger',
                    },
                },
            },
        });
    }

    public openConfirmWithNotePopup(
        confirmFunction: () => void,
        pending$?: Observable<boolean> | undefined,
        noteRequired?: boolean | undefined,
        resetFieldAfterSubmit?: boolean | undefined,
    ): void {
        this.clearTriggerFocus();

        this.#dialog.open(DialogV2, {
            panelClass: DIALOG_PANEL_CLASS,
            width: DialogWidth.SM,
            data: {
                component: ConfirmWithNoteDialogComponent,
                params: {
                    confirmFunction,
                    pending$,
                    noteRequired,
                    resetFieldAfterSubmit,
                    title: marker('global.labels.attention'),
                    description: marker('modal.confirm.text.delete'),
                    icon: {
                        variant: BsIconTypes.ExclamationTriangle,
                        style: 'danger',
                    },
                },
            },
        });
    }

    public openDialog(
        component: unknown,
        params?: Pick<DialogParams, 'serviceParams' | 'title' | 'description' | 'icon' | 'dialogContentPaddingClass'>,
        paramsDialog?: MatDialogConfig,
    ): MatDialogRef<unknown> {
        this.clearTriggerFocus();

        return this.#dialog.open(DialogV2, {
            ...{
                panelClass: DIALOG_PANEL_CLASS,
                width: DialogWidth.SM,
                data: {
                    component,
                    params,
                },
            },
            ...paramsDialog,
        });
    }

    private clearTriggerFocus(): void {
        const activeElement = document.activeElement as HTMLElement;

        if (activeElement) {
            activeElement.blur();
        }
    }
}
